import { datevConfigsProvider, datevExportRunsProvider } from "./datevProvider";
import { serviceEntriesProvider } from "./serviceEntriesProvider";
import {
  CreateParams,
  DataProvider,
  DeleteManyParams,
  DeleteParams,
  GetListParams,
  GetManyParams,
  GetManyReferenceParams,
  GetOneParams,
  UpdateManyParams,
  UpdateParams,
} from "react-admin";
import { appliedReferralCodesProvider } from "./appliedReferralCodesProvider";
import { referralCodeUsagesProvider } from "./referralCodeUsagesProvider";
import { autovioEmployeesProvider } from "./autovioEmployeesProvider";
import { branchesProvider } from "./branchesProvider";
import { catalogProvider } from "./catalogProvider";
import { courseParticipantsProvider } from "./courseParticipantsProvider";
import { drivingLessonsProvider } from "./drivingLessonsProvider";
import { drivingLicenseAuthorityFormsProvider } from "./drivingLicenseAuthorityFormsProvider";
import { drivingLicenseAuthorityInstructionsProvider } from "./drivingLicenseAuthorityInstructionsProvider";
import { drivingLicenseAuthoritiesProvider } from "./drivingLicenseAuthoritiesProvider";
import { drivingSchoolsProvider } from "./drivingSchoolsProvider";
import { examLocationsProvider } from "./examLocationsProvider";
import { invoicesProvider } from "./invoicesProvider";
import { theoryEventsProvider } from "./theoryEventsProvider";
import { instructorsProvider } from "./instructorsProvider";
import { studentsProvider } from "./studentsProvider";
import { usersProvider } from "./usersProvider";
import { instructorFavoriteLocationsProvider } from "./instructorFavoriteLocationsProvider";
import { adasProvider } from "./adasProvider";
import { balanceProvider } from "./balanceProvider";
import { paymentsProvider } from "./paymentsProvider";
import { saldoProvider } from "./saldoProvider";
import { bookedQuotesProvides } from "./bookedQuotesProvider";
import { calendarEventsProvider } from "./calendarEventsProvider";
import { coursesProvider } from "./coursesProvider";
import { courseLessonsProvider } from "./courseLessonsProvider";
import { theoryExamSimulationResultsProvider } from "./theoryExamSimulationResultsProvider";
import { usersToBeDeletedProvider } from "./usersToBeDeletedProvider";
import { inAppNotificationsProvider } from "./inAppNotificationsProvider";
import { instructorPrivateAppointmentsProvider } from "./instructorPrivateAppointmentsProvider";
import { instructorWorkTimesProvider } from "./instructorWorkTimesProvider";
import { backendUserProvider } from "./backendUserProvider";
import { instructorRecommendationSettingsProvider } from "./instructorRecommendationSettingsProvider";
import { monthlyFinanceReportsProvider, monthlyPayoutReportsProvider } from "./financeReportsProvider";
import { drivingSchoolNotesProvider, studentNotesProvider } from "./notesProvider";
import { drivingSchoolDocumentsProvider, studentDocumentsProvider, threadDocumentsProvider } from "./documentsProvider";
import { studentStickyNotesProvider } from "./studentStickyNotesProvider";
import { workflowsProvider } from "./workflowsProvider";
import { promoCodesProvider } from "./promoCodesProvider";
import { dunningProcessesProvider } from "./dunningProcessesProvider";
import { studentDrivingLessonsProvider } from "./studentDrivingLessonsProvider";
import { calendarEventHistoryProvider } from "./calendarEventHistoryProvider";
import { performanceOverviewProvider } from "./performanceOverviewProvider";
import { autovioPayoutProvider } from "./autovioPayoutProvider.js";
import { invoiceCustomersProvider } from "./invoiceCustomersProvider";
import { performanceOverviewReportsProvider } from "./performanceOverviewReportsProvider";
import { autovioPayoutItemProvider } from "./autovioPayoutItemProvider.js";
import { creditNotesProvider } from "./creditNotesProvider.js";
import { taxConsultantExportsProvider } from "./taxConsultantsExportsProvider.js";
import { startingPointsProvider } from "./startingPointsProvider.js";
import { studentActivitiesProvider } from "./studentActivitiesProvider";
import { threadsProvider } from "./threadsProvider.js";
import { playgroundProvider } from "./playgroundProvider";
import { employeesProvider } from "./employeesProvider";
import { drivingSchoolLabelsProvider } from "./drivingSchoolLabelsProvider";
import { postsProvider } from "./postsProvider";
import { maunalRefundProvider } from "./manualRefundProvider";
import { manualPaymentsProvider } from "./manualPaymentsProvider";
import { timetablesProvider } from "./timetablesProvider";
import { courseTemplatesProvider } from "./courseTemplatesProvider";
import { autoFormsProvider } from "./autoFormsProvider";
import { roomsProvider } from "./roomsProvider";
import { subscriptionPackagesProvider } from "./subscriptionPackagesProvider";
import { examAuthorityFeesProvider } from "./examAuthorityFeesProvider";
import { vehiclesProvider } from "./vehiclesProvider";
import { drivingSchoolOtherEventCategoriesProvider } from "./drivingSchoolOtherEventCategoriesProvider";
import { drivingSchoolServiceTypesProvider } from "./drivingSchoolServiceTypesProvider";
import { drivingSchoolCustomCardsProvider } from "./drivingSchoolCustomCardsProvider";
import { graduatedInvoiceCustomersProvider } from "./GraduatedInvoiceCustomersProvider";
import { theoryQuestionsProvider } from "./theoryQuestionsProvider";
import * as Sentry from "@sentry/react";
import _isEqual from "lodash/isEqual";
import type { AbstractDataProvider } from "./AbstractDataProvider";
import { markDataLoaded, markLoadingData } from "../trackStartupPerformance";

let _numPendingQueries = 0;
let _dataLoadedTimeout: undefined | ReturnType<typeof setTimeout>;

const _dataProviders: Array<
  [DataProvider | (() => Promise<{ default: AbstractDataProvider<any> }>), string, ...string[]]
> = [
  [appliedReferralCodesProvider as DataProvider, "appliedReferralCodes"],
  [referralCodeUsagesProvider as DataProvider, "referralCodeUsages"],
  [backendUserProvider as DataProvider, "backendUser", "fundingInstructions"],
  [branchesProvider as DataProvider, "branches"],
  [courseParticipantsProvider as DataProvider, "courseParticipants"],
  [drivingLicenseAuthorityFormsProvider as DataProvider, "drivingLicenseAuthorityForms"],
  [drivingLicenseAuthorityInstructionsProvider as DataProvider, "drivingLicenseAuthorityInstructions"],
  [drivingLicenseAuthoritiesProvider as DataProvider, "drivingLicenseAuthorities"],
  [employeesProvider as DataProvider, "employees"],
  [drivingSchoolsProvider as DataProvider, "drivingSchools"],
  [examLocationsProvider as DataProvider, "examLocations"],
  [usersProvider as DataProvider, "users"],
  [instructorsProvider as DataProvider, "instructors"],
  [studentsProvider as DataProvider, "students", "examinationAssignments"],
  [invoiceCustomersProvider as DataProvider, "invoiceCustomers"],
  [graduatedInvoiceCustomersProvider as DataProvider, "graduatedInvoiceCustomers"],
  [usersToBeDeletedProvider as DataProvider, "usersToBeDeleted"],
  [catalogProvider, "areas", "products", "bundles", "trainings", "offerFeedback"],
  [adasProvider, "adas"],
  [
    vehiclesProvider as DataProvider,
    "vehicles",
    "cars",
    "motorcycles",
    "trailers",
    "simulators",
    "trucks",
    "buses",
    "tractors",
  ],
  [autovioEmployeesProvider as DataProvider, "autovioEmployees"],
  [paymentsProvider as DataProvider, "payments"],
  [saldoProvider as DataProvider, "saldo"],
  [balanceProvider as DataProvider, "balances"],
  [monthlyFinanceReportsProvider, "monthlyFinanceReports"],
  [monthlyPayoutReportsProvider, "monthlyPayoutReports"],
  [invoicesProvider, "b2bInvoices", "b2cInvoices", "studentBalance"],
  [creditNotesProvider, "b2bCreditNotes", "b2cCreditNotes"],
  [calendarEventsProvider as DataProvider, "calendarEvents"],
  [drivingLessonsProvider as DataProvider, "drivingLessons"],
  [studentDrivingLessonsProvider as DataProvider, "studentDrivingLessons"],
  [theoryEventsProvider as DataProvider, "theoryLessons", "theoryEvents"],
  [courseLessonsProvider as DataProvider, "courseLessons"],
  [coursesProvider as DataProvider, "courses"],
  [instructorPrivateAppointmentsProvider as DataProvider, "instructorPrivateAppointments"],
  [instructorRecommendationSettingsProvider as DataProvider, "instructorRecommendationSettings"],
  [instructorWorkTimesProvider as DataProvider, "instructorWorkTimes"],
  [bookedQuotesProvides as DataProvider, "bookedQuotes"],
  [theoryExamSimulationResultsProvider as DataProvider, "theoryExamSimulationResults"],
  [instructorFavoriteLocationsProvider as DataProvider, "instructorFavoriteLocations"],
  [inAppNotificationsProvider as DataProvider, "inAppNotifications"],
  [drivingSchoolLabelsProvider as DataProvider, "labels"],
  [drivingSchoolOtherEventCategoriesProvider as DataProvider, "otherEventCategories"],
  [datevConfigsProvider as DataProvider, "datevConfigs"],
  [datevExportRunsProvider as DataProvider, "datevExportRuns"],
  [serviceEntriesProvider as DataProvider, "serviceEntries"],
  [drivingSchoolServiceTypesProvider as DataProvider, "serviceTypes"],
  [drivingSchoolCustomCardsProvider as DataProvider, "customCards"],
  [studentNotesProvider as DataProvider, "studentNotes"],
  [drivingSchoolNotesProvider as DataProvider, "drivingSchoolNotes"],
  [postsProvider as DataProvider, "posts"],
  [threadsProvider as DataProvider, "threads"],
  [studentDocumentsProvider as DataProvider, "studentDocuments"],
  [drivingSchoolDocumentsProvider as DataProvider, "drivingSchoolDocuments"],
  [threadDocumentsProvider as DataProvider, "threadDocuments"],
  [studentStickyNotesProvider as DataProvider, "studentStickyNotes"],
  [workflowsProvider as DataProvider, "workflows"],
  [promoCodesProvider as DataProvider, "promoCodes"],
  [dunningProcessesProvider as DataProvider, "dunningProcesses"],
  [maunalRefundProvider, "manualRefunds"],
  [manualPaymentsProvider, "manualPayments"],
  [calendarEventHistoryProvider as DataProvider, "calendarEventHistory"],
  [performanceOverviewProvider as DataProvider, "performanceOverview", "advancePayments", "openInvoices"],
  [performanceOverviewReportsProvider as DataProvider, "performanceOverviewReports"],
  [autovioPayoutProvider as DataProvider, "autovioPayouts"],
  [autovioPayoutItemProvider as DataProvider, "autovioPayoutItems"],
  [taxConsultantExportsProvider as DataProvider, "taxConsultantExports"],
  [startingPointsProvider as DataProvider, "startingPoints"],
  [studentActivitiesProvider as DataProvider, "studentActivities"],
  [playgroundProvider as DataProvider, "playground"],
  [timetablesProvider as DataProvider, "timetables"],
  [courseTemplatesProvider as DataProvider, "courseTemplates"],
  [autoFormsProvider as DataProvider, "autoForms"],
  [roomsProvider as DataProvider, "rooms"],
  [subscriptionPackagesProvider as DataProvider, "subscriptionPackages"],
  [examAuthorityFeesProvider as DataProvider, "examAuthorityFees"],
  [theoryQuestionsProvider as DataProvider, "theoryQuestions"],
  [() => import("./vogelFahrschuelerProvider"), "vogelFahrschueler"],
];

class CompositeDataProvider {
  private dataProviders: Record<
    string,
    DataProvider | Promise<AbstractDataProvider<any>> | (() => Promise<{ default: AbstractDataProvider<any> }>)
  > = {};

  constructor() {
    for (const [dataProvider, ...resources] of _dataProviders) {
      for (const resource of resources) {
        if (this.dataProviders[resource]) {
          throw new Error(`Duplicate dataProvider for resource: ${resource}`);
        }
        this.dataProviders[resource] = dataProvider;
      }
    }
  }

  async _delegate(methodName: string, resource: string, params: any, call: string) {
    console.info(`${call} ...`);
    if (methodName.startsWith("get")) {
      if (_numPendingQueries++ === 0) {
        clearTimeout(_dataLoadedTimeout);
        markLoadingData();
      }
    }
    try {
      let dataProvider = this.dataProviders[resource];
      if (isPromise(dataProvider)) {
        dataProvider = await (dataProvider as Promise<DataProvider>);
      } else if (typeof dataProvider === "function") {
        const promise = dataProvider().then((module) => module.default);
        this.dataProviders[resource] = promise;
        dataProvider = (await promise) as DataProvider;
      }
      if (!dataProvider) {
        throw new Error(`Unknown resource: ${resource}`);
      }
      // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
      const method = dataProvider[methodName] as Function;
      const t0 = Date.now();
      let warningReported = false;
      const intervalId = setInterval(() => {
        const dt = Math.round((Date.now() - t0) / 1000);
        console.info(`${call} is still running after ${dt} s`);
        if (dt > 30 && !warningReported) {
          warningReported = true;
          Sentry.captureMessage(`dataProvider.${methodName}(...) takes longer than 30s!`, "error");
        }
      }, 1000);
      try {
        const result = await method.bind(dataProvider, resource, params)();
        const durationInSeconds = (Date.now() - t0) / 1000;
        const durationFormated = `${(Math.round(durationInSeconds * 10) / 10).toFixed(1)} s`;
        const records = methodName === "getOne" ? result && [result.data] : methodName.startsWith("get") && result.data;
        if (Array.isArray(records)) {
          if (records.length === 1) {
            console.info(`${call} => retrieved 1 record in ${durationFormated}`);
          } else {
            console.info(`${call} => retrieved ${result.data.length} records in ${durationFormated}`);
          }
        } else {
          console.info(`${call} took ${durationFormated}`);
        }
        return result;
      } finally {
        clearInterval(intervalId);
      }
    } catch (error) {
      console.error(`${call} failed:`, error);
      throw error;
    } finally {
      if (methodName.startsWith("get")) {
        if (--_numPendingQueries === 0) {
          const now = performance.now();
          _dataLoadedTimeout = setTimeout(() => markDataLoaded(now), 250);
        }
      }
    }
  }

  async getList(resource: string, params: GetListParams) {
    // Keep the log message short if there are objects in params.meta ...
    const { meta, ...params_ } = params;
    if (meta) {
      (params_ as any).meta = Object.fromEntries(
        Object.entries(meta).map(([k, v]) => [k, typeof v === "object" ? "..." : v]),
      );
    }
    const call = `dataProvider.getList(${JSON.stringify(resource)}, ${JSON.stringify(params_)})`;
    const result = await this._delegate("getList", resource, params, call);
    return result;
  }

  async getOne(resource: string, params: GetOneParams) {
    const call = `dataProvider.getOne(${JSON.stringify(resource)}, ${JSON.stringify(params)})`;
    const result = await this._delegate("getOne", resource, params, call);
    return result;
  }

  async getMany(resource: string, params: GetManyParams) {
    const { ids, ...restParams } = params;
    const params_ =
      ids.length <= 3
        ? JSON.stringify(params)
        : (() => {
            const s = JSON.stringify(restParams);
            if (s === "{}") {
              return `{"ids":[... ${ids.length} ids ...]}`;
            } else {
              return `{"ids":[... ${ids.length} ids ...],${s.substring(1)}}`;
            }
          })();
    const call = `dataProvider.getMany(${JSON.stringify(resource)}, ${params_})`;
    const result = await this._delegate("getMany", resource, params, call);
    return result;
  }

  async getManyReference(resource: string, params: GetManyReferenceParams) {
    const call = `dataProvider.getManyReference(${JSON.stringify(resource)}, ${JSON.stringify(params)})`;
    const result = await this._delegate("getManyReference", resource, params, call);
    return result;
  }

  create(resource: string, params: CreateParams) {
    const call = `dataProvider.create(${JSON.stringify(resource)}, ${JSON.stringify(params)})`;
    return this._delegate("create", resource, params, call);
  }

  update(resource: string, params: UpdateParams) {
    const { id, data, previousData, meta } = params;
    let params_: string;
    if (previousData) {
      const delta = Object.fromEntries(Object.entries(data).filter(([k, v]) => !_isEqual(v, previousData[k])));
      params_ = `id: ${JSON.stringify(id)}, delta: ${JSON.stringify(delta)}`;
      if (meta) {
        params_ += `, meta: ${JSON.stringify(meta)}`;
      }
    } else {
      params_ = JSON.stringify(params);
    }
    const call = `dataProvider.update(${JSON.stringify(resource)}, ${params_})`;
    return this._delegate("update", resource, params, call);
  }

  updateMany(resource: string, params: UpdateManyParams) {
    const call = `dataProvider.updateMany(${JSON.stringify(resource)}, ${JSON.stringify(params)})`;
    return this._delegate("updateMany", resource, params, call);
  }

  delete(resource: string, params: DeleteParams) {
    const call = `dataProvider.delete(${JSON.stringify(resource)}, ${JSON.stringify(params)})`;
    return this._delegate("delete", resource, params, call);
  }

  deleteMany(resource: string, params: DeleteManyParams) {
    const call = `dataProvider.deleteMany(${JSON.stringify(resource)}, ${JSON.stringify(params)})`;
    return this._delegate("deleteMany", resource, params, call);
  }
}

export const compositeDataProvider = new CompositeDataProvider() as DataProvider;

function isPromise(x: any): x is Promise<unknown> {
  return x && typeof x.then === "function" && typeof x.catch === "function";
}
